import {
    fetchProductTypes,
    fetchFacilities,
    fetchDevices,
    fetchMerchantUsers,
    fetchUserGroups,
} from '../services/newCoreApi.js';
import { buildMap, buildUserMap, buildUserGroupMap } from '../services/referenceMaps.js';
import { fetchUserKeys } from './userKeys.js';
import {
    REQUEST_REF_MAPS,
    RECEIVE_REF_MAPS,
    RECEIVE_REF_MAPS_ERROR,
    RESET_REF_MAPS,
} from './discountServiceTypes.js';

/**
 * Builds the id-translation maps the discount mapper needs, once per organization.
 *
 * Deliberately a separate slice from `products` / `cabinets` / `productCategories`: those are
 * shared by other pages and keep feeding the pickers from the legacy endpoints. Nothing here
 * supplies a display field — only `legacyId <-> cdsUuid` translation.
 *
 * Categories need no map at all: the sync writes `product_attribute.id` as the Selfly category
 * `_id` verbatim, so legacy category ids pass straight through.
 */

export {
    REQUEST_REF_MAPS,
    RECEIVE_REF_MAPS,
    RECEIVE_REF_MAPS_ERROR,
    RESET_REF_MAPS,
} from './discountServiceTypes.js';

function requestRefMaps() {
    return { type: REQUEST_REF_MAPS };
}

function receiveRefMaps(maps, groupId) {
    return { type: RECEIVE_REF_MAPS, maps, groupId, receivedAt: Date.now() };
}

function receiveRefMapsError(error) {
    return { type: RECEIVE_REF_MAPS_ERROR, error };
}

/** Clear the maps — dispatched on organization switch. */
export function resetRefMaps() {
    return { type: RESET_REF_MAPS };
}

/**
 * Thunk: build all three maps for the resolved merchant. No-ops when already built for this org
 * or a request is in flight, so callers can invoke it freely.
 *
 * Requires `state.merchants.merchant` — dispatch `fetchMerchant()` first.
 * @returns {function}
 */
export function fetchRefMaps() {
    return async (dispatch, getState) => {
        const { refMaps, merchants, user } = getState();
        const merchant = merchants && merchants.merchant;
        const groupId = user && user.group && user.group.Id;
        if (!merchant) {
            return null;
        }
        if (refMaps.fetching) {
            return refMaps.maps;
        }
        if (refMaps.fetched && refMaps.groupId === groupId) {
            return refMaps.maps;
        }

        dispatch(requestRefMaps());
        try {
            // The user map is driven by the org's known userKeys (mapper rule 7: match forward
            // from the key, never derive one from a merchant_user row), so they must be loaded
            // first. `discounts.jsx` also fetches them for its picker, but the two are unordered —
            // and `fetchUserKeys` is a no-op-ish repeat rather than a conflict.
            if (!getState().userKeys.fetched) {
                await dispatch(fetchUserKeys());
            }

            // Every `/data/*` path takes the merchant **urlname** — cds and core-api both resolve
            // `WHERE name = ?` against a column that holds husky's urlname. The display name
            // yields a raw NoSuchElementException (500), not a clean 404.
            const [productTypes, facilities, devices, merchantUsers, userGroups] = await Promise.all([
                fetchProductTypes(merchant.urlname),
                fetchFacilities(merchant.urlname),
                fetchDevices(merchant.urlname),
                fetchMerchantUsers(merchant.urlname),
                fetchUserGroups(merchant.urlname),
            ]);

            const knownUserKeys = ((getState().userKeys && getState().userKeys.userKeys) || [])
                .map(row => row && row.userKey)
                .filter(Boolean);

            const maps = {
                // `sourceExternalId` is the legacy product id; `id` is `product_type.id`, exactly
                // the UUID a cds PRODUCT_TYPE target wants.
                products: buildMap(productTypes, 'sourceExternalId'),
                // `reference` is the legacy Selfly `location._id` — a free-form string, so never
                // filter it by UUID shape (real values include codes like `Carlsberg.Kerava`).
                facilities: buildMap(facilities, 'reference'),
                // `name` is the canonical device code. NOT `deviceId`, which is the underlying
                // husky fridge UUID and would target nothing.
                devices: buildMap(devices, 'name'),
                // userKey ↔ `merchant_user.id`. `ambiguous` holds keys that matched more than one
                // row; the mapper refuses those rather than picking one.
                users: buildUserMap(merchantUsers, knownUserKeys),
                // `{groupId: [merchantUserId]}` — the hop from a cds USER_GROUP target to people.
                userGroups: buildUserGroupMap(userGroups),
            };

            dispatch(receiveRefMaps(maps, groupId));
            return maps;
        } catch (error) {
            dispatch(receiveRefMapsError(error));
            return null;
        }
    };
}
